import { useCallback, useEffect, useRef, useState } from 'react'

/** Horizontal scroll-snap carousel: tracks the centered slide and exposes prev/next/goTo. */
export function useCarousel(count: number, initial = 0) {
  const trackRef = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState(initial)

  const goTo = useCallback((index: number, smooth = true) => {
    const track = trackRef.current
    if (!track) return
    const slide = track.children[index] as HTMLElement | undefined
    if (!slide) return
    const left = slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2
    track.scrollTo({ left, behavior: smooth ? 'smooth' : 'auto' })
  }, [])

  useEffect(() => {
    if (initial > 0) goTo(initial, false)
  }, [initial, goTo])

  useEffect(() => {
    const track = trackRef.current
    if (!track) return
    const onScroll = () => {
      const center = track.scrollLeft + track.clientWidth / 2
      let closest = 0
      let min = Infinity
      Array.from(track.children).forEach((child, i) => {
        const el = child as HTMLElement
        const d = Math.abs(el.offsetLeft + el.clientWidth / 2 - center)
        if (d < min) {
          min = d
          closest = i
        }
      })
      setActive(closest)
    }
    track.addEventListener('scroll', onScroll, { passive: true })
    return () => track.removeEventListener('scroll', onScroll)
  }, [])

  const prev = () => goTo(Math.max(0, active - 1))
  const next = () => goTo(Math.min(count - 1, active + 1))

  return { trackRef, active, goTo, prev, next }
}
